'use client';

import { create } from 'zustand';

import type { TtsWordInfo } from './types';

const useTtsStore = create<{ wordInfo: TtsWordInfo | null; playbackErrors: number }>()(() => ({
  wordInfo: null,
  playbackErrors: 0,
}));

/**
 * Publishes the currently-spoken TTS word to all `TtsHighlighter` subscribers.
 *
 * rAF/cleanup paths repeatedly call this with the same value (most commonly
 * `null → null` during teardown). The rAF tracker only fires on segment-index
 * change so non-null values are always fresh objects — a reference check is
 * sufficient and avoids notifying subscribers for the no-ops.
 */
export function updateTtsWordInfo(info: TtsWordInfo | null): void {
  useTtsStore.setState(state => (state.wordInfo === info ? state : { wordInfo: info }));
}

export function useTtsWordInfo(): TtsWordInfo | null {
  return useTtsStore(s => s.wordInfo);
}

/**
 * The runtime drops `thread.speech` on the `ended` status, so a failed utterance
 * never reaches message state. A counter, not a flag, so consecutive failures
 * each toast.
 */
export function reportTtsPlaybackError(): void {
  useTtsStore.setState(s => ({ playbackErrors: s.playbackErrors + 1 }));
}

export function useTtsPlaybackErrors(): number {
  return useTtsStore(s => s.playbackErrors);
}
